import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Wifi } from "lucide-react";

import { api, ApiError, type SiteBandwidth } from "../../api";
import { Select } from "../../components/Select";
import { EmptyState, Note, SettingsSection } from "./shared";

// Typical CCTV H.264 at medium motion, Mbps at (15 fps, 30 fps). H.265 ~0.6x.
// Same table as docs/BANDWIDTH.md and the api's estimate for unmeasured cameras.
const RATES: Array<{ height: number; label: string; r15: number; r30: number }> = [
  { height: 360, label: "360p", r15: 0.4, r30: 0.7 },
  { height: 720, label: "720p", r15: 1.0, r30: 1.8 },
  { height: 1080, label: "1080p", r15: 2.0, r30: 4.0 },
  { height: 1440, label: "4 MP (1440p)", r15: 3.5, r30: 6.0 },
  { height: 2160, label: "4K", r15: 7.0, r30: 12.0 },
];
const HEADROOM = 1.5;

export function streamMbps(height: number, fps: number, codec: "h264" | "h265"): number {
  const row = RATES.find((r) => height <= r.height) ?? RATES[RATES.length - 1];
  const base =
    fps <= 15 ? (row.r15 * fps) / 15 : row.r15 + ((row.r30 - row.r15) * (Math.min(fps, 30) - 15)) / 15;
  return codec === "h265" ? base * 0.6 : base;
}

const mbps = (bps: number | null | undefined) =>
  bps ? `${(bps / 1e6).toFixed(bps < 1e6 ? 2 : 1)} Mbps` : "—";

const STATUS: Record<SiteBandwidth["status"], { tone: string; text: string }> = {
  ok: { tone: "bg-emerald-500/15 text-emerald-300", text: "Fits" },
  tight: { tone: "bg-amber-500/15 text-amber-200", text: "Tight" },
  over: { tone: "bg-red-500/15 text-red-300", text: "Too slow" },
  unknown: { tone: "bg-white/10 text-bf-muted", text: "Enter the upload speed" },
};

/** What each location's cameras send against its internet upload, plus a
 *  planner for a site that is not connected yet. */
export function InternetSection({ canEdit }: { canEdit: boolean }) {
  const sites = useQuery({
    queryKey: ["bandwidth-sites"],
    queryFn: api.bandwidth.sites,
    refetchInterval: 15_000,
  });
  return (
    <SettingsSection
      title="Internet speed"
      description="Every camera reaches IntraScene over its location's upload link. Check that each location can carry its cameras, and plan new ones."
      hint={
        <>
          Analysis streams of AI cameras flow all the time. Watching in <b>Original</b>{" "}
          adds the watched stream; <b>Live</b> and <b>Detections</b> add nothing to the
          location's upload. Required upload is the streams' total times {HEADROOM}, to
          leave room for peaks and the rest of the business's traffic.
        </>
      }
    >
      <Planner />
      {sites.isError && (
        <Note tone="error">
          {sites.error instanceof ApiError ? sites.error.message : "Could not load locations."}
        </Note>
      )}
      {sites.data && sites.data.length === 0 && (
        <EmptyState icon={Wifi} title="No locations yet" />
      )}
      {sites.data?.map((site) => (
        <SiteCard key={site.store} site={site} canEdit={canEdit} />
      ))}
    </SettingsSection>
  );
}

function SiteCard({ site, canEdit }: { site: SiteBandwidth; canEdit: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(site.uplink_mbps ? String(site.uplink_mbps) : "");
  const save = useMutation({
    mutationFn: () => api.bandwidth.setUplink(site.store, draft.trim() ? Number(draft) : null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bandwidth-sites"] }),
  });
  const status = STATUS[site.status];
  const ai = site.cameras.filter((c) => c.role !== "view_only");
  const usedPct =
    site.uplink_mbps && site.needed_mbps
      ? Math.min(100, Math.round((site.needed_mbps / site.uplink_mbps) * 100))
      : 0;

  return (
    <section className="bf-card space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-white">{site.display_name}</div>
          <div className="text-sm text-bf-muted">
            {ai.length} AI camera{ai.length === 1 ? "" : "s"} send {mbps(site.analysis_bps)}
            {site.unmeasured > 0 && ` (${site.unmeasured} not measured yet)`}; needs{" "}
            <b className="text-bf-text">{site.needed_mbps.toFixed(1)} Mbps</b> upload.
          </div>
        </div>
        <span className={"rounded px-2 py-0.5 text-xs " + status.tone}>{status.text}</span>
      </div>

      {site.uplink_mbps ? (
        <div className="h-2 overflow-hidden rounded-full bg-white/5">
          <div
            className={
              "h-full " +
              (site.status === "over"
                ? "bg-red-500"
                : site.status === "tight"
                  ? "bg-amber-400"
                  : "bg-emerald-500")
            }
            style={{ width: `${usedPct}%` }}
          />
        </div>
      ) : null}

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <label>
          <span className="mb-1 block text-xs text-bf-muted">Measured upload speed (Mbps)</span>
          <input
            className="bf-input w-40"
            type="number"
            min={1}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={!canEdit}
            placeholder="from a speed test"
          />
        </label>
        {canEdit && (
          <button className="bf-button disabled:opacity-40" type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </button>
        )}
      </form>

      {site.cameras.length > 0 && (
        <table className="w-full text-left text-sm">
          <thead className="text-xs text-bf-muted">
            <tr>
              <th className="py-1 font-medium">Camera</th>
              <th className="py-1 font-medium">Analysis (main)</th>
              <th className="py-1 font-medium">Sub stream</th>
            </tr>
          </thead>
          <tbody>
            {site.cameras.map((c) => (
              <tr key={c.id} className="border-t border-white/5">
                <td className="py-1.5">
                  {c.name}
                  {c.role === "view_only" && <span className="ml-2 text-xs text-bf-muted">live view only</span>}
                </td>
                <td className="py-1.5">
                  {c.role === "view_only" ? "—" : mbps(c.main_bps)}
                  {c.estimated && <span className="ml-1 text-xs text-bf-muted">typical</span>}
                </td>
                <td className="py-1.5">{mbps(c.sub_bps)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Planner() {
  const [cams, setCams] = useState(16);
  const [height, setHeight] = useState(1080);
  const [fps, setFps] = useState(15);
  const [codec, setCodec] = useState<"h264" | "h265">("h265");
  const [watched, setWatched] = useState(0);
  const [uplink, setUplink] = useState("");

  const per = streamMbps(height, fps, codec);
  const streams = per * cams;
  const required = streams * HEADROOM + per * watched;
  const have = Number(uplink) || 0;

  return (
    <section className="bf-card space-y-3">
      <div className="font-semibold text-white">Plan a location</div>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <label>
          <span className="mb-1 block text-xs text-bf-muted">AI cameras</span>
          <input
            className="bf-input"
            type="number"
            min={1}
            value={cams}
            onChange={(e) => setCams(Math.max(1, Number(e.target.value) || 1))}
          />
        </label>
        <div>
          <span className="mb-1 block text-xs text-bf-muted">Picture</span>
          <Select
            ariaLabel="Picture"
            value={String(height)}
            onChange={(v) => setHeight(Number(v))}
            options={RATES.map((r) => ({ value: String(r.height), label: r.label }))}
          />
        </div>
        <div>
          <span className="mb-1 block text-xs text-bf-muted">Frame rate</span>
          <Select
            ariaLabel="Frame rate"
            value={String(fps)}
            onChange={(v) => setFps(Number(v))}
            options={[5, 10, 15, 20, 25, 30].map((f) => ({ value: String(f), label: `${f} fps` }))}
          />
        </div>
        <div>
          <span className="mb-1 block text-xs text-bf-muted">Codec</span>
          <Select
            ariaLabel="Codec"
            value={codec}
            onChange={(v) => setCodec(v as "h264" | "h265")}
            options={[
              { value: "h265", label: "H.265" },
              { value: "h264", label: "H.264" },
            ]}
          />
        </div>
        <label>
          <span className="mb-1 block text-xs text-bf-muted">Watched in Original</span>
          <input
            className="bf-input"
            type="number"
            min={0}
            value={watched}
            onChange={(e) => setWatched(Math.max(0, Number(e.target.value) || 0))}
          />
        </label>
        <label>
          <span className="mb-1 block text-xs text-bf-muted">Upload speed (Mbps)</span>
          <input
            className="bf-input"
            type="number"
            min={0}
            value={uplink}
            onChange={(e) => setUplink(e.target.value)}
            placeholder="optional"
          />
        </label>
      </div>
      <p className="text-sm text-bf-muted">
        About <b className="text-bf-text">{per.toFixed(1)} Mbps</b> per camera,{" "}
        <b className="text-bf-text">{streams.toFixed(0)} Mbps</b> of streams: the location needs{" "}
        <b className="text-bf-text">{Math.ceil(required)} Mbps</b> upload.
        {have > 0 &&
          (have >= required ? (
            <span className="ml-1 text-emerald-300">{have} Mbps is enough.</span>
          ) : (
            <span className="ml-1 text-red-300">
              {have} Mbps is not enough: lower the frame rate, analyse the sub stream, or use H.265.
            </span>
          ))}
      </p>
    </section>
  );
}
